import {
  SettingsPermissions,
  UserPermissions,
} from "@redotech/account-common/permissions";
import { isRedoEmployeeEmail } from "@redotech/account-common/redo-employee";
import { GetUser } from "@redotech/account-common/user";
import { customsReadinessPopupFlag } from "@redotech/redo-return-core-common/returns-feature-flags";
import { TeamContextValue } from "@redotech/redo-web/context/team-context";
import {
  CustomsReadinessClient,
  customsReadinessPopupContent,
  loadCustomsReadinessForSession,
} from "./customs-readiness-popup";
import { parseOnboardingPreviewType } from "./onboarding-preview";

type TeamType = NonNullable<TeamContextValue>;

export function isReturnsEnabled(team: TeamType): boolean {
  return team.settings.returns?.enabled === true;
}

export function canManageInternationalReturnsSettings(
  permissions: Set<UserPermissions> | undefined,
): boolean {
  return (
    permissions?.has(SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS) ===
      true ||
    permissions?.has(SettingsPermissions.SET_SHIPPING_SETTINGS) === true
  );
}

const INTERNATIONAL_RETURNS_SETTINGS_PATH = "/settings/international/returns";

export function isOnInternationalReturnsSettingsPage(
  pathname: string,
): boolean {
  const index = pathname.indexOf(INTERNATIONAL_RETURNS_SETTINGS_PATH);
  if (index === -1) {
    return false;
  }

  const suffix = pathname.slice(
    index + INTERNATIONAL_RETURNS_SETTINGS_PATH.length,
  );
  return suffix === "" || suffix.startsWith("/");
}

export function isEligibleForInternationalReturnsCustomsPopup(
  team: TeamType,
  permissions: Set<UserPermissions> | undefined,
  pathname: string,
  search = "",
): boolean {
  if (
    typeof navigator !== "undefined" &&
    navigator.webdriver &&
    parseOnboardingPreviewType(search) === null
  ) {
    return false;
  }

  if (!isReturnsEnabled(team)) {
    return false;
  }

  if (!canManageInternationalReturnsSettings(permissions)) {
    return false;
  }

  if (isOnInternationalReturnsSettingsPage(pathname)) {
    return false;
  }

  return true;
}

/** Shown to non-Redo users on flagged teams (or in preview) while something is
 * stuck in customs or a required customs field is missing from the catalog. */
export async function isEligibleForCustomsReadinessPopup(
  team: TeamType,
  user: Pick<GetUser, "email"> | undefined,
  permissions: Set<UserPermissions> | undefined,
  platformRpcClient: CustomsReadinessClient | undefined,
  pathname: string,
  search = "",
): Promise<boolean> {
  const flagOn =
    team.featureFlags?.[customsReadinessPopupFlag.key] === true ||
    parseOnboardingPreviewType(search) !== null;
  if (
    !flagOn ||
    isRedoEmployeeEmail(user?.email) ||
    !platformRpcClient ||
    !isEligibleForInternationalReturnsCustomsPopup(
      team,
      permissions,
      pathname,
      search,
    )
  ) {
    return false;
  }
  const readiness = await loadCustomsReadinessForSession(
    platformRpcClient,
    team._id,
  );
  return readiness !== null && customsReadinessPopupContent(readiness) !== null;
}
